import type { SvgIconComponent } from "@mui/icons-material"
import AssessmentIcon from "@mui/icons-material/Assessment"
import EventAvailableIcon from "@mui/icons-material/EventAvailable"
import FactCheckIcon from "@mui/icons-material/FactCheck"
import GroupIcon from "@mui/icons-material/Group"
import InfoIcon from "@mui/icons-material/Info"
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks"
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium"
import SchoolIcon from "@mui/icons-material/School"
import SummarizeIcon from "@mui/icons-material/Summarize"

export type ReportPage = {
  title: string
  pageTitle: string
  description: string
  endpointDescription: string
  path: string
  icon: SvgIconComponent
}

// Keep these descriptions aligned with packages/report-app/api-docs/openapi.yml.
export const courseReportPage: ReportPage = {
  title: "Courses",
  pageTitle: "Courses Report",
  description: "Review courses created by the active organization",
  endpointDescription:
    "Returns courses created by the specified organization, including objective, publishing, timing, and delivery details.",
  path: "/reports/course",
  icon: LibraryBooksIcon,
}

export const eventReportPage: ReportPage = {
  title: "Events",
  pageTitle: "Events Report",
  description: "Review events created by the active organization",
  endpointDescription:
    "Returns events created by the specified organization, including timing, registration, capacity, and meeting details.",
  path: "/reports/event",
  icon: EventAvailableIcon,
}

export const learningPathSummaryReportPage: ReportPage = {
  title: "Learning Paths",
  pageTitle: "Learning Paths Report",
  description: "Review learning paths created by the active organization",
  endpointDescription:
    "Returns learning paths created by the specified organization, including status, visibility, and course count details.",
  path: "/reports/learning-path",
  icon: SchoolIcon,
}

export const organizationReportPage: ReportPage = {
  title: "Org Info",
  pageTitle: "Organization Information",
  description: "View configuration for the active organization",
  endpointDescription:
    "Returns configuration information for the specified organization.",
  path: "/reports/organization",
  icon: InfoIcon,
}

export const userReportPage: ReportPage = {
  title: "Users",
  pageTitle: "Users Report",
  description: "Review users and group memberships in the active organization",
  endpointDescription:
    "Returns users that belong to the specified organization, including identity fields and group references.",
  path: "/reports/user",
  icon: GroupIcon,
}

export const userEventCreditTypeReportPage: ReportPage = {
  title: "Credit Type",
  pageTitle: "User Event Credit Type Report",
  description: "Summarize PD credits and taxonomy time by user",
  endpointDescription:
    'Returns a list of users with attended user event counts for the specified organization. Results can be filtered by optional event start-date range and include a credit-type breakdown keyed by "field_taxonomy_name".',
  path: "/reports/user-event-credit-type",
  icon: AssessmentIcon,
}

export const userEventAttendanceReportPage: ReportPage = {
  title: "Attendance",
  pageTitle: "User Event Attendance Report",
  description: "See org events in a date range and who is marked attended",
  endpointDescription:
    "Returns users in the specified organization together with all org events in the optional date range, whether each user is marked attended for each event, and the total attended event count per user.",
  path: "/reports/user-event-attendance",
  icon: EventAvailableIcon,
}

export const userEventRsvpStatusReportPage: ReportPage = {
  title: "RSVP Status",
  pageTitle: "User Event RSVP Status Report",
  description: "See each user's RSVP status across org events in a date range",
  endpointDescription:
    "Returns users in the specified organization together with all org events in the optional date range, each user's RSVP status per event, and the total count of events with a known RSVP per user.",
  path: "/reports/user-event-rsvp-status",
  icon: FactCheckIcon,
}

export const eventAttendanceReportPage: ReportPage = {
  title: "Attendance",
  pageTitle: "Event User Attendance Summary",
  description: "Compare RSVP and attendance counts for each org event",
  endpointDescription:
    "Returns events in the specified organization within the optional date range, including how many users have a known RSVP and how many users are marked attended for each event.",
  path: "/reports/event-user-attendance-summary",
  icon: SummarizeIcon,
}

export const userCourseReportPage: ReportPage = {
  title: "Courses",
  pageTitle: "User Course Report",
  description: "Track user course progress and completion",
  endpointDescription:
    "Review learner progress on courses owned by the specified organization, including user and course details and start, completion, and update dates.",
  path: "/reports/user-course",
  icon: LibraryBooksIcon,
}

export const userLearningPathReportPage: ReportPage = {
  title: "Learning Path",
  pageTitle: "User Learning Path Report",
  description: "Track user progress and completion across learning paths",
  endpointDescription:
    "Returns a list of started or completed learning paths, including user details, status, completion information, and time tracking.",
  path: "/reports/user-learning-path",
  icon: SchoolIcon,
}

export const userMandatedTrainingReportPage: ReportPage = {
  title: "Mandated Training",
  pageTitle: "User Mandated Training Report",
  description: "See when users became compliant for mandated training series",
  endpointDescription:
    "Returns one row per organization user with eligible mandated training series and each user's in-compliance date. The optional date range filters by mandated training deadline date.",
  path: "/reports/user-mandated-training",
  icon: WorkspacePremiumIcon,
}

export const userEventReports: ReportPage[] = [
  userEventRsvpStatusReportPage,
  userEventAttendanceReportPage,
  userEventCreditTypeReportPage,
]

export const eventReports: ReportPage[] = [
  eventReportPage,
  eventAttendanceReportPage,
]

export const userReports: ReportPage[] = [
  userCourseReportPage,
  userEventAttendanceReportPage,
  userEventCreditTypeReportPage,
  userEventRsvpStatusReportPage,
  userLearningPathReportPage,
  userMandatedTrainingReportPage,
]

export const organizationReports: ReportPage[] = [
  organizationReportPage,
  courseReportPage,
  learningPathSummaryReportPage,
  userReportPage,
]
